import type { ListFieldConfig } from '@ztechapps/common.hook'
import { ListFieldTypeEnum } from '@ztechapps/common.hook'

// Credits and markup are fractional (a single call costs a fraction of a credit) — the
// currency's own display decimals (usually 2) would collapse every row to 0.00, so the usage
// table always renders both at a fixed 4 places regardless of the currency's own precision.
export const USAGE_CREDIT_DECIMALS = 4

// The key of the user column in `usageListFieldConfig` — dropped from the table when the app bills
// each user their own balance, where every row would carry the same user.
export const USAGE_USER_FIELD = 'createdByName'

// Total Tokens, Markup and User are computed/resolver-only values (inputTokens + outputTokens is
// summed client-side; markupCcy and createdByName are `@FieldResolver`s, not stored/indexed Mongo
// fields) — none can be pushed down as a server-side search/sort/filter, so all are excluded. The
// user is narrowed by the dedicated user filter beside the table, which filters on the stored
// `createdBy` id.
// `credits`/`markupCcy` are denominated in the account's own currency, but this config's labels
// stay currency-agnostic — `ListHeader.tsx` is the sole owner of the dynamic `Credits (<CCY>)`/
// `Markup (<CCY>)` labels, reading `currency` live off context. This static module-level config
// is one shared constant across every owner's table — baking a currency into a field's own
// `displayName` here would show one owner's currency to another.
export const usageListFieldConfig: ListFieldConfig = {
  createdAt: {
    name: 'createdAt',
    displayName: 'Date',
    type: ListFieldTypeEnum.dateTimeZone,
    excludeOnSearch: true,
    size: 180,
    index: 0,
  },
  createdByName: {
    name: USAGE_USER_FIELD,
    displayName: 'User',
    excludeOnSearch: true,
    excludeOnSort: true,
    excludeOnFilter: true,
    size: 220,
    index: 1,
  },
  feature: { name: 'feature', displayName: 'Feature', defaultOnSearch: true, size: 200, index: 2 },
  remarks: { name: 'remarks', displayName: 'Remarks', size: 260, index: 3 },
  model: { name: 'model', displayName: 'Model', size: 200, index: 4 },
  inputTokens: {
    name: 'inputTokens',
    displayName: 'Input Tokens',
    type: ListFieldTypeEnum.number,
    size: 130,
    index: 5,
  },
  outputTokens: {
    name: 'outputTokens',
    displayName: 'Output Tokens',
    type: ListFieldTypeEnum.number,
    size: 130,
    index: 6,
  },
  totalTokens: {
    name: 'totalTokens',
    displayName: 'Total Tokens',
    excludeOnSearch: true,
    excludeOnSort: true,
    excludeOnFilter: true,
    size: 130,
    index: 7,
  },
  rawUsd: { name: 'rawUsd', displayName: 'Cost USD', type: ListFieldTypeEnum.number, size: 130, index: 8 },
  markupCcy: {
    name: 'markupCcy',
    displayName: 'Markup',
    excludeOnSearch: true,
    excludeOnSort: true,
    excludeOnFilter: true,
    // A few px wider than the other 130-wide columns so the dynamic `Markup (<CCY>)` label
    // ListHeader.tsx renders here fits next to the sort icon without wrapping to 2 lines.
    size: 160,
    index: 9,
  },
  credits: {
    name: 'credits',
    displayName: 'Credits',
    type: ListFieldTypeEnum.number,
    // Same widening as markupCcy, for the `Credits (<CCY>)` label.
    size: 160,
    index: 10,
  },
  balanceAfter: {
    name: 'balanceAfter',
    displayName: 'Balance After',
    type: ListFieldTypeEnum.number,
    // Same widening as the school Ledger tab's own `balanceAfter` column (ledger/constants.ts) —
    // "Balance After (<CCY>)" is visibly longer than "Credits (<CCY>)".
    size: 210,
    index: 11,
  },
}
